import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import FeedCard, { unlockFeedAudio } from "@/components/FeedCard";
import CommentsSheet from "@/components/CommentsSheet";

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [commentPost, setCommentPost] = useState(null);
  const container = useRef(null);

  const load = useCallback(async (skip) => {
    setLoading(true);
    try {
      const { data } = await api.get("/feed", { params: { skip, limit: 10 } });
      setPosts((p) => (skip === 0 ? data : [...p, ...data]));
      if (data.length < 10) setDone(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  useEffect(() => {
    const events = ["pointerdown", "keydown", "touchend"];
    const unlock = () => { unlockFeedAudio(); events.forEach((e) => window.removeEventListener(e, unlock, true)); };
    events.forEach((e) => window.addEventListener(e, unlock, true));
    return () => events.forEach((e) => window.removeEventListener(e, unlock, true));
  }, []);

  useEffect(() => {
    const root = container.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(Number(e.target.dataset.index))),
      { root, threshold: 0.6 }
    );
    root.querySelectorAll("[data-index]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [posts]);

  useEffect(() => {
    if (!done && !loading && posts.length && active >= posts.length - 2) load(posts.length);
  }, [active, posts.length, done, loading, load]);

  const bumpComments = () =>
    setPosts((ps) => ps.map((p) => (p.id === commentPost?.id ? { ...p, comments_count: p.comments_count + 1 } : p)));

  return (
    <div ref={container} className="h-[calc(100dvh-7.5rem)] snap-y snap-mandatory overflow-y-scroll overscroll-contain scrollbar-none [-webkit-overflow-scrolling:touch] md:h-[calc(100dvh-3.5rem)]" data-testid="feed-container">
      {posts.length === 0 && (
        <div className="flex h-full items-center justify-center text-gray-500" data-testid="feed-empty">
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : "No posts yet."}
        </div>
      )}
      {posts.map((p, i) => (
        <div key={p.id} data-index={i} className="h-full snap-start snap-always">
          <FeedCard post={p} active={i === active} near={Math.abs(i - active) <= 1} onComments={setCommentPost} />
        </div>
      ))}
      <CommentsSheet post={commentPost} open={!!commentPost} onOpenChange={(o) => !o && setCommentPost(null)} onAdded={bumpComments} />
    </div>
  );
}
